import { useState } from "react";
import { Box, Flex, Heading, Stack, Text, HStack, Icon } from "@chakra-ui/react";
import { Lock, SlidersHorizontal } from "lucide-react";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { FeatureToggle } from "@/components/FeatureToggle";
import { colors } from "@/theme/colors";

const TOGGLABLE_ROLES = ["supervisor", "field_employee"];

// Tier 2 — the Org Admin decides which of their OWN roles can use each
// feature the company is entitled to. Tier 1 always wins: features the
// company doesn't have are locked here. Org Admins always keep access.
export default function Settings() {
  const { featureCatalog, features, activeCompanyId, reloadFeatures } = useAuth();
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  const toggleRole = async (featureKey, roleKey) => {
    const current = features?.[featureKey]?.roles?.[roleKey] !== false;
    setBusy(`${featureKey}:${roleKey}`);
    setErr(null);
    const { error } = await supabase.from("feature_role_access").upsert({
      company_id: activeCompanyId,
      feature_key: featureKey,
      role: roleKey,
      enabled: !current,
      updated_at: new Date().toISOString(),
    });
    setBusy(null);
    if (error) { setErr(error.message); return; }
    reloadFeatures();
  };

  return (
    <Box data-testid="settings-page">
      <HStack gap="3" mb="1">
        <Icon as={SlidersHorizontal} boxSize="6" color={colors.teal} />
        <Heading fontFamily="heading" fontSize="2xl" color={colors.textPrimary}>Company Settings</Heading>
      </HStack>
      <Text fontSize="sm" color={colors.textSecondary} mb="6">
        Choose which roles in your company can see and use each feature. You always keep access yourself.
      </Text>

      {err && (
        <Box bg={colors.criticalBg} color={colors.criticalText} borderRadius="10px" px="4" py="2.5" mb="4" fontSize="sm" data-testid="settings-error">
          {err}
        </Box>
      )}

      <Stack gap="3" maxW="640px">
        {(featureCatalog || []).map((f) => {
          const entitled = Boolean(features?.[f.key]?.entitled);
          return (
            <Box key={f.key} bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4"
              opacity={entitled ? 1 : 0.6} data-testid={`settings-feature-${f.key}`}>
              <Flex justify="space-between" align="flex-start" gap="3">
                <Box minW="0">
                  <HStack gap="2">
                    <Text fontWeight="600" color={colors.textPrimary}>{f.label}</Text>
                    {!entitled && (
                      <HStack gap="1" bg={colors.cream} border="1px solid" borderColor={colors.border} borderRadius="full" px="2" py="0.5">
                        <Icon as={Lock} boxSize="3" color={colors.textSecondary} />
                        <Text fontSize="10px" fontWeight="600" color={colors.textSecondary} data-testid={`feature-locked-${f.key}`}>
                          Not in your plan
                        </Text>
                      </HStack>
                    )}
                  </HStack>
                  <Text fontSize="sm" color={colors.textSecondary}>{f.description}</Text>
                  {!entitled && (
                    <Text fontSize="xs" color={colors.textSecondary} mt="1" fontStyle="italic">
                      Contact Hakina Safety to add this feature to your plan.
                    </Text>
                  )}
                </Box>
              </Flex>

              {entitled && (
                <Stack gap="2" mt="3" pt="3" borderTop="1px solid" borderColor={colors.border}>
                  {TOGGLABLE_ROLES.map((r) => {
                    const on = features?.[f.key]?.roles?.[r] !== false;
                    return (
                      <Flex key={r} align="center" justify="space-between">
                        <Text fontSize="sm" color={colors.textPrimary}>{ROLE_LABELS[r]}s</Text>
                        <FeatureToggle
                          on={on}
                          disabled={busy === `${f.key}:${r}`}
                          onChange={() => toggleRole(f.key, r)}
                          testId={`role-toggle-${f.key}-${r}`}
                        />
                      </Flex>
                    );
                  })}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
